/**
 * RS PDP — gallery, linked-product pickers, ATC, sticky bar.
 * Data from #rs-pdp-data (Liquid-generated JSON).
 */
(function () {
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

  const dataEl = document.getElementById("rs-pdp-data");
  if (!dataEl) return;

  let data;
  try {
    data = JSON.parse(dataEl.textContent);
  } catch (e) {
    console.error("RS PDP: invalid product data", e);
    return;
  }

  const root = document.querySelector(".rs-pdp");
  if (!root) return;

  const moneyOptions = () => ({
    currencyCodeEnabled: data.currencyCodeEnabled,
    currencyCode: data.currencyCode,
    locale: data.locale,
  });

  const formatMoney = (cents) =>
    window.RsMoney?.format(cents, moneyOptions()) ?? String(cents);

  const findProduct = (handle) => {
    if (handle === data.current.handle) return data.current;
    return (
      data.dials.find((p) => p.handle === handle) ||
      data.bezels.find((p) => p.handle === handle)
    );
  };

  const resolveBezelLabel = (product) => {
    if (!product) return "";
    if (product.bezelLabel) return product.bezelLabel;
    const match = data.bezels?.find((b) => b.handle === product.handle);
    return match?.label || "";
  };

  const resolveInitialBundleQty = () => {
    // Never pre-select a bundle — extras stay hidden until the shopper taps an option.
    return 0;
  };

  const state = {
    currentHandle: data.current.handle,
    previewHandle: null,
    activeGalleryId: "hero",
    bundleQty: resolveInitialBundleQty(),
    extraQty: Object.fromEntries(data.bezels.map((b) => [b.handle, 0])),
  };

  const activeProduct = () => findProduct(state.previewHandle ?? state.currentHandle) || data.current;

  const dialProducts = () => data.dials.filter((p) => p.images?.length);
  const bezelProducts = () => data.bezels.filter((p) => p.images?.length);

  const Gallery = window.RsPdpGallery || {};
  const PLAY_ICON = Gallery.PLAY_ICON || "";
  const isVideo = Gallery.isVideo || ((item) => item?.mediaType === "video" || item?.mediaType === "external_video");
  const buildVideoMedia =
    Gallery.buildVideoMedia ||
    (() => {
      const wrap = document.createElement("div");
      return wrap;
    });
  const renderHeroMediaShared = Gallery.renderHeroMedia;
  const mapProductImages = Gallery.mapProductImages;

  const galleryImages = (product) =>
    mapProductImages
      ? mapProductImages(product)
      : (product?.images || []).map((img, i) => ({
          id: i === 0 ? "hero" : String(img.id),
          src: img.src,
          alt: img.alt || product.title,
          mediaType: img.mediaType || "image",
          sources: img.sources || [],
          embedUrl: img.embedUrl || "",
        }));

  const renderHeroMedia = (item, animate = true, forcePlay = false) => {
    const fig = $("#gallery-hero", root);
    if (renderHeroMediaShared) {
      renderHeroMediaShared(fig, item, {
        autoplay: data.videoAutoplay,
        animate,
        forcePlay,
      });
      return;
    }
    if (!fig || !item) return;
    if (isVideo(item)) {
      fig.innerHTML = "";
      fig.appendChild(buildVideoMedia(item, data.videoAutoplay, forcePlay));
      return;
    }
    let img = $("#hero-image", fig);
    if (!img) {
      fig.innerHTML =
        '<img id="hero-image" width="1200" height="1200" decoding="async" alt="" />';
      img = $("#hero-image", fig);
    }
    if (!item.src) return;
    if (animate) img.classList.add("is-swapping");
    img.src = item.src;
    img.alt = item.alt;
    if (animate) requestAnimationFrame(() => img.classList.remove("is-swapping"));
  };

  const renderGallery = () => {
    const product = activeProduct();
    const images = galleryImages(product);
    if (!images.length) return;

    const hero = images[0];
    renderHeroMedia(hero, !state.previewHandle);

    const grid = $("#gallery-grid", root);
    if (grid) {
      grid.innerHTML = "";
      images.slice(1).forEach((item) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = `gallery__thumb${state.activeGalleryId === item.id ? " is-active" : ""}${isVideo(item) ? " gallery__thumb--video" : ""}`;
        btn.setAttribute("aria-label", `View ${item.alt}`);
        btn.innerHTML = `<img src="${item.src.replace("width=1200", "width=400")}" alt="" loading="lazy" decoding="async" width="400" height="400" />${isVideo(item) ? `<span class="gallery__thumb-play">${PLAY_ICON}</span>` : ""}`;
        btn.addEventListener("click", () => {
          state.activeGalleryId = item.id;
          renderHeroMedia(item, true, isVideo(item));
          $$(".gallery__thumb", grid).forEach((el) => el.classList.remove("is-active"));
          btn.classList.add("is-active");
          $("#gallery-hero", root)?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
        grid.appendChild(btn);
      });
    }

    renderMobileSlider(images);
  };

  const getCurrentSlide = () => {
    const track = $("#slider-track", root);
    if (!track?.firstElementChild) return 0;
    const slideWidth = track.firstElementChild.offsetWidth;
    return Math.round(track.scrollLeft / slideWidth);
  };

  const scrollToSlide = (index) => {
    const track = $("#slider-track", root);
    if (!track) return;
    const slides = $$(".gallery-slider__slide", track);
    const clamped = Math.max(0, Math.min(index, slides.length - 1));
    const slideWidth = slides[0].offsetWidth;
    track.scrollTo({ left: clamped * slideWidth, behavior: "smooth" });
  };

  const onSliderScroll = () => {
    const dots = $$(".gallery-slider__dot", root);
    const current = getCurrentSlide();
    dots.forEach((dot, i) => dot.classList.toggle("is-active", i === current));
  };

  const renderMobileSlider = (images) => {
    const track = $("#slider-track", root);
    const dotsContainer = $("#slider-dots", root);
    if (!track || !dotsContainer) return;

    track.innerHTML = "";
    dotsContainer.innerHTML = "";

    images.forEach((item, i) => {
      const slide = document.createElement("div");
      slide.className = "gallery-slider__slide";
      if (isVideo(item)) {
        slide.appendChild(buildVideoMedia(item, data.videoAutoplay));
      } else {
        slide.innerHTML = `<img src="${item.src}" alt="${item.alt}" loading="${i === 0 ? "eager" : "lazy"}" decoding="async" width="800" height="800" />`;
      }
      track.appendChild(slide);

      const dot = document.createElement("button");
      dot.type = "button";
      dot.className = `gallery-slider__dot${i === 0 ? " is-active" : ""}`;
      dot.setAttribute("role", "tab");
      dot.setAttribute("aria-label", `Slide ${i + 1}`);
      dot.addEventListener("click", () => scrollToSlide(i));
      dotsContainer.appendChild(dot);
    });

    const prevBtn = $("#slider-prev", root);
    const nextBtn = $("#slider-next", root);
    if (prevBtn) prevBtn.onclick = () => scrollToSlide(getCurrentSlide() - 1);
    if (nextBtn) nextBtn.onclick = () => scrollToSlide(getCurrentSlide() + 1);

    track.removeEventListener("scroll", onSliderScroll);
    track.addEventListener("scroll", onSliderScroll, { passive: true });
  };

  const updateLabels = () => {
    const product = findProduct(state.currentHandle) || data.current;
    const dialLabel = $("#dial-label", root);
    const bezelLabel = $("#bezel-label", root);
    const title = $("#product-title", root);
    const stickyName = $("#sticky-name");
    const stickyImage = $("#sticky-image");

    if (title) title.textContent = product.title;
    if (dialLabel && product.dialLabel) dialLabel.textContent = product.dialLabel;
    if (bezelLabel) {
      const label = resolveBezelLabel(product);
      if (label) bezelLabel.textContent = label;
    }

    const mountedBezelLabel = resolveBezelLabel(product);
    if (stickyName) {
      stickyName.textContent = mountedBezelLabel
        ? `${product.title} · ${mountedBezelLabel} bezel`
        : product.title;
    }
    if (stickyImage && product.featuredImage) {
      stickyImage.src = product.featuredImage.replace("width=1200", "width=120");
      stickyImage.alt = product.title;
    }
  };

  const totalExtraQty = () =>
    Object.entries(state.extraQty).reduce((sum, [handle, n]) => {
      if (handle === state.currentHandle) return sum;
      return sum + n;
    }, 0);

  /** Bundle qty = extra bezels on top of the watch (1 → 1 extra, 2 → 2, …). */
  const extraSlots = () => Math.max(0, state.bundleQty || 0);

  const availableExtraBezels = () =>
    (data.bezels || []).filter(
      (bezel) =>
        bezel.handle !== state.currentHandle &&
        (bezel.bezelProductVariantId || bezel.variantId)
    );

  const clearExtras = () => {
    (data.bezels || []).forEach((bezel) => {
      state.extraQty[bezel.handle] = 0;
    });
  };

  /** Pre-check the first N available extras for the chosen bundle tier. */
  const preselectExtras = (count) => {
    clearExtras();
    availableExtraBezels().slice(0, Math.max(0, count)).forEach((bezel) => {
      state.extraQty[bezel.handle] = 1;
    });
  };

  const activeBundle = () => data.bundles?.find((b) => b.qty === state.bundleQty) ?? null;

  const isIndependentPack = () => data.purchaseLayout === "independent";

  const extraBezelUnitPrice = () => data.extraBezelUnitPrice || 0;

  const bundlePricing = (bundle) => ({
    price: bundle.price,
    compareAt: bundle.compareAt,
  });

  const packIsComplete = () => {
    const slots = extraSlots();
    return slots > 0 && totalExtraQty() === slots;
  };

  const packCost = () => {
    const bundle = activeBundle();
    if (!bundle || state.bundleQty === 0) return 0;
    return bundle.price;
  };

  const packCompareAt = () => {
    const bundle = activeBundle();
    if (!bundle || state.bundleQty === 0) return 0;
    const { price, compareAt } = bundlePricing(bundle);
    return compareAt > price ? compareAt : 0;
  };

  /** Combined mode only — extras add onto the main ATC. */
  const extraBezelCost = () => {
    if (isIndependentPack()) return 0;
    const bundle = activeBundle();
    if (!bundle || state.bundleQty === 0 || totalExtraQty() === 0) return 0;
    return bundle.price;
  };

  const orderTotal = () => {
    const product = findProduct(state.currentHandle) || data.current;
    return product.price + extraBezelCost();
  };

  const orderCompareAt = () => {
    const product = findProduct(state.currentHandle) || data.current;
    if (isIndependentPack()) {
      return product.compareAtPrice > product.price ? product.compareAtPrice : 0;
    }

    const productCompare =
      product.compareAtPrice > product.price ? product.compareAtPrice : product.price;
    const extrasCost = extraBezelCost();

    if (!extrasCost) {
      return product.compareAtPrice > product.price ? product.compareAtPrice : 0;
    }

    const bundle = activeBundle();
    const { price, compareAt } = bundlePricing(bundle);
    const extrasCompare = Math.max(compareAt || 0, price);
    const totalCompare = productCompare + extrasCompare;
    const totalPrice = product.price + price;
    return totalCompare > totalPrice ? totalCompare : 0;
  };

  const isPromoActive = () => {
    const pct = Number(data.promoDiscountPercent) || 0;
    if (!data.promoEnabled || pct <= 0) return false;
    const now = Date.now();
    if (data.promoEndMs && now >= data.promoEndMs) return false;
    if (data.promoStartMs && now < data.promoStartMs) return false;
    return true;
  };

  const applyPromoPricing = (total, compareAt) => {
    if (!isPromoActive()) return { price: total, compareAt: compareAt || 0 };
    const pct = Number(data.promoDiscountPercent) || 0;
    const crossed = total;
    const sale = Math.round((crossed * (100 - pct)) / 100);
    return { price: sale, compareAt: crossed };
  };

  const updatePriceDisplay = (priceEl, compareEl, price, compareAt) => {
    if (priceEl) priceEl.textContent = formatMoney(price);
    if (compareEl) {
      if (compareAt > price) {
        compareEl.textContent = formatMoney(compareAt);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }

    const savingsEl = $("#price-savings", root);
    const savingsAmount = $("#price-savings-amount", root);
    const savings = compareAt > price ? compareAt - price : 0;
    if (savingsEl && savingsAmount) {
      if (savings > 0) {
        savingsAmount.textContent = formatMoney(savings);
        savingsEl.hidden = false;
      } else {
        savingsEl.hidden = true;
      }
    }
  };

  const updateActionPrices = (price, compareAt) => {
    const pairs = [
      ["#cta-total", "#cta-compare"],
      ["#sticky-total", "#sticky-total-compare"],
      ["#sticky-price", "#sticky-compare"],
    ];

    pairs.forEach(([priceSel, compareSel]) => {
      const priceEl = document.querySelector(priceSel);
      const compareEl = document.querySelector(compareSel);
      if (priceEl) priceEl.textContent = formatMoney(price);
      if (!compareEl) return;
      if (compareAt > price) {
        compareEl.textContent = formatMoney(compareAt);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    });
  };

  const updateTotals = () => {
    const total = orderTotal();
    const compareAt = orderCompareAt();
    const priced = applyPromoPricing(total, compareAt);
    const watchPrice = $("#watch-price", root);

    updatePriceDisplay(watchPrice, $("#watch-compare-price", root), priced.price, priced.compareAt);
    updateActionPrices(priced.price, priced.compareAt);
    updatePackAtc();
  };

  const updatePackAtc = () => {
    if (!isIndependentPack()) return;

    const btn = $("#pack-add-to-cart", root);
    const hint = $("#pack-atc-hint", root);
    const totalEl = $("#pack-cta-total", root);
    const compareEl = $("#pack-cta-compare", root);
    if (!btn) return;

    const complete = packIsComplete();
    const price = packCost();
    const compareAt = packCompareAt();

    if (totalEl) totalEl.textContent = formatMoney(price);
    if (compareEl) {
      if (compareAt > price) {
        compareEl.textContent = formatMoney(compareAt);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }

    btn.disabled = !complete;
    btn.setAttribute("aria-disabled", String(!complete));

    if (hint) {
      if (state.bundleQty === 0) {
        hint.textContent = "Choose a pack size to get started";
        hint.hidden = false;
      } else if (!complete) {
        hint.textContent = data.packIncompleteLabel || "Select all bezels in your pack to continue";
        hint.hidden = false;
      } else {
        hint.hidden = true;
      }
    }
  };

  const renderDialSwatches = () => {
    const row = $("#dial-group .swatch-row", root);
    if (!row || !data.dials.length) return;

    row.innerHTML = "";
    data.dials.forEach((dial) => {
      const isCurrent = state.currentHandle === dial.handle;
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "swatch";
      btn.role = "radio";
      btn.setAttribute("aria-checked", String(isCurrent));
      btn.setAttribute("aria-label", dial.label);
      btn.style.setProperty("--swatch-color", dial.color);
      btn.title = dial.label;

      btn.addEventListener("mouseenter", () => {
        state.previewHandle = dial.handle;
        state.activeGalleryId = "hero";
        renderGallery();
        const dialLabel = $("#dial-label", root);
        if (dialLabel) dialLabel.textContent = dial.label;
      });

      btn.addEventListener("mouseleave", () => {
        state.previewHandle = null;
        state.activeGalleryId = "hero";
        renderGallery();
        const current = findProduct(state.currentHandle) || data.current;
        const dialLabel = $("#dial-label", root);
        if (dialLabel && current.dialLabel) dialLabel.textContent = current.dialLabel;
      });

      btn.addEventListener("click", () => {
        window.location.href = dial.url;
      });

      row.appendChild(btn);
    });
  };

  const updateExtrasStatus = () => {
    const slots = extraSlots();
    const text = $("#extras-status-text", root);
    if (text) {
      text.textContent = `${totalExtraQty()} ${data.extrasStatusOf || "of"} ${slots} ${data.extrasStatusSuffix || "extras selected"}`;
    }

    const status = $("#extras-status", root);
    const selected = $("#extras-selected", root);
    const target = $("#extras-target", root);
    if (status) {
      status.hidden = slots === 0;
      if (selected) selected.textContent = String(totalExtraQty());
      if (target) target.textContent = String(slots);
      status.classList.toggle("is-complete", slots > 0 && totalExtraQty() === slots);
    }
  };

  const renderBezelSwatches = () => {
    const row = $("#bezel-group .swatch-row--bezel", root) || $("#bezel-group .swatch-row", root);
    if (!row || !data.bezels.length) return;

    row.innerHTML = "";
    data.bezels.forEach((bezel) => {
      const isCurrent = state.currentHandle === bezel.handle;
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "swatch swatch--bezel";
      btn.role = "radio";
      btn.setAttribute("aria-checked", String(isCurrent));
      btn.setAttribute("aria-label", `${bezel.label} bezel on watch`);
      if (bezel.thumb) {
        btn.style.setProperty("--swatch-image", `url("${bezel.thumb}")`);
      }
      if (bezel.color) {
        btn.style.setProperty("--swatch-color", bezel.color);
      }
      btn.title = bezel.label;

      btn.addEventListener("mouseenter", () => {
        state.previewHandle = bezel.handle;
        state.activeGalleryId = "hero";
        renderGallery();
        const bezelLabel = $("#bezel-label", root);
        if (bezelLabel) bezelLabel.textContent = bezel.label;
      });

      btn.addEventListener("mouseleave", () => {
        state.previewHandle = null;
        state.activeGalleryId = "hero";
        renderGallery();
        const current = findProduct(state.currentHandle) || data.current;
        const bezelLabel = $("#bezel-label", root);
        if (bezelLabel) {
          const label = resolveBezelLabel(current);
          if (label) bezelLabel.textContent = label;
        }
      });

      btn.addEventListener("click", () => {
        if (isCurrent || !bezel.url) return;
        window.location.href = bezel.url;
      });

      row.appendChild(btn);
    });
  };

  const renderExtrasPicker = () => {
    const group = $("#extras-group", root);
    const grid = $("#extras-picker", root);
    const panel = $("#extras-panel", root) || group?.closest(".extras");
    if (!group || !grid || !data.showExtras) return;

    const slots = extraSlots();
    const open = state.bundleQty > 0 && slots > 0 && data.bezels.length > 0;
    group.hidden = !open;
    if (panel) panel.classList.toggle("is-open", open);
    if (!open) {
      updateExtrasStatus();
      return;
    }

    grid.innerHTML = "";
    const atCap = totalExtraQty() >= slots;
    const addLabel = data.bezelAddLabel || "Add";

    data.bezels
      .filter((b) => b.handle !== state.currentHandle)
      .forEach((bezel) => {
        const isExtra = state.extraQty[bezel.handle] > 0;
        const cell = document.createElement("div");
        cell.className = "bezel-picker";

        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "swatch swatch--bezel";
        btn.setAttribute("aria-hidden", "true");
        btn.tabIndex = -1;
        if (bezel.thumb) btn.style.setProperty("--swatch-image", `url("${bezel.thumb}")`);

        const checkLabel = document.createElement("label");
        checkLabel.className = "bezel-picker__check is-visible";

        const checkbox = document.createElement("input");
        checkbox.type = "checkbox";
        checkbox.className = "bezel-picker__input";
        checkbox.checked = isExtra;
        checkbox.disabled = !isExtra && atCap;
        checkbox.setAttribute("aria-label", `Add ${bezel.label} as extra bezel`);

        checkbox.addEventListener("change", () => {
          if (checkbox.checked && totalExtraQty() >= slots) {
            checkbox.checked = false;
            return;
          }
          state.extraQty[bezel.handle] = checkbox.checked ? 1 : 0;
          renderExtrasPicker();
          updateTotals();
        });

        const checkText = document.createElement("span");
        checkText.textContent = bezel.label || addLabel;

        checkLabel.append(checkbox, checkText);
        cell.append(btn, checkLabel);

        if (bezel.bezelProductUrl && bezel.bezelProductPrice > 0) {
          const shopLink = document.createElement("a");
          shopLink.className = "bezel-picker__link";
          shopLink.href = bezel.bezelProductUrl;
          shopLink.textContent = formatMoney(bezel.bezelProductPrice);
          shopLink.setAttribute("aria-label", `Shop ${bezel.label} bezel separately`);
          cell.appendChild(shopLink);
        }

        grid.appendChild(cell);
      });

    updateExtrasStatus();
  };

  const renderBundles = () => {
    const container = $("#bezel-bundles", root);
    const extrasSection = container?.closest(".extras");
    if (!container || !data.bundles?.length || !extraBezelUnitPrice()) {
      if (extrasSection) extrasSection.hidden = true;
      return;
    }
    if (extrasSection) extrasSection.hidden = false;

    container.innerHTML = "";
    data.bundles.forEach((bundle) => {
      const selected = state.bundleQty === bundle.qty;
      const { price, compareAt } = bundlePricing(bundle);
      const savings = compareAt - price;
      const percent =
        compareAt > price
          ? Math.round((1 - price / compareAt) * 100)
          : 0;
      const label = bundle.label || (bundle.qty === 1 ? "1 bezel" : `${bundle.qty} bezels`);
      const popularLabel = data.bundlePopularLabel || "Popular";
      const saveLabel = data.bundleSaveLabel || "Save";
      const offLabel = data.bundleOffLabel || "";

      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `bezel-bundle${selected ? " is-selected" : ""}${bundle.popular ? " is-popular" : ""}`;
      btn.setAttribute("role", "radio");
      btn.setAttribute("aria-checked", String(selected));
      btn.dataset.qty = String(bundle.qty);

      btn.innerHTML = `
        ${bundle.popular ? `<span class="bezel-bundle__badge">${popularLabel}</span>` : ""}
        <span class="bezel-bundle__label">${label}</span>
        ${savings > 0 ? `<span class="bezel-bundle__compare">${formatMoney(compareAt)}</span>` : ""}
        <span class="bezel-bundle__price">${formatMoney(price)}</span>
        ${savings > 0 ? `<span class="bezel-bundle__save">${saveLabel} ${formatMoney(savings)} · ${percent}%${offLabel ? ` ${offLabel}` : ""}</span>` : ""}
      `;

      btn.addEventListener("click", () => {
        if (state.bundleQty === bundle.qty) {
          state.bundleQty = 0;
          clearExtras();
        } else {
          state.bundleQty = bundle.qty;
          preselectExtras(bundle.qty);
        }
        renderBundles();
        renderExtrasPicker();
        updateTotals();
      });

      container.appendChild(btn);
    });
  };

  const getCartDrawerSectionIds = () => {
    const sections = [];
    document.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } })
    );

    if (!sections.length) {
      const drawer = document.getElementById("cart-drawer") || document.querySelector("cart-drawer");
      const sectionEl = drawer?.closest("[id^='shopify-section-']");
      if (sectionEl) {
        sections.push(sectionEl.id.replace("shopify-section-", ""));
      }
    }

    return sections;
  };

  const fetchCartDrawerSections = async (sectionIds) => {
    const shopRoot = window.Shopify?.routes?.root || "/";
    const sections = {};
    const bust = Date.now();

    await Promise.all(
      sectionIds.map(async (id) => {
        try {
          const jsonRes = await fetch(
            `${shopRoot}?sections=${encodeURIComponent(id)}&_=${bust}`,
            { credentials: "same-origin", headers: { Accept: "application/json" } }
          );
          if (jsonRes.ok) {
            const json = await jsonRes.json();
            if (json?.[id]) {
              sections[id] = json[id];
              return;
            }
          }
        } catch {
          /* fall through */
        }

        const response = await fetch(
          `${shopRoot}?section_id=${encodeURIComponent(id)}&_=${bust}`,
          { credentials: "same-origin" }
        );
        sections[id] = await response.text();
      })
    );

    return sections;
  };

  const buildWatchCartItems = () => {
    const product = findProduct(state.currentHandle) || data.current;
    return product.variantId ? [{ id: product.variantId, quantity: 1 }] : [];
  };

  const buildPackCartItems = () => {
    const items = [];
    if (!data.showExtras || totalExtraQty() === 0) return items;

    data.bezels.forEach((bezel) => {
      if (
        bezel.handle !== state.currentHandle &&
        state.extraQty[bezel.handle] > 0 &&
        bezel.bezelProductVariantId
      ) {
        items.push({ id: bezel.bezelProductVariantId, quantity: 1 });
      }
    });

    return items;
  };

  const buildCartItems = () => {
    if (isIndependentPack()) return buildWatchCartItems();

    const items = buildWatchCartItems();
    return items.concat(buildPackCartItems());
  };

  const postCartAdd = async (items, btn) => {
    if (!items.length) return false;

    const sectionIds = getCartDrawerSectionIds();
    const originalText = btn?.innerHTML;
    if (btn) {
      btn.disabled = true;
      btn.setAttribute("aria-busy", "true");
    }

    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

    try {
      const shopRoot = window.Shopify?.routes?.root || "/";
      const payload = { items };

      if (sectionIds.length) {
        payload.sections = sectionIds.join(",");
        payload.sections_url = `${window.location.pathname}${window.location.search}`;
      }

      const response = await fetch(`${shopRoot}cart/add.js`, {
        method: "POST",
        body: JSON.stringify(payload),
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      const responseJson = await response.json();
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));

      if (!response.ok) {
        throw new Error(responseJson?.description || responseJson?.message || "Add to cart failed");
      }

      const cartContent = await (
        await fetch(`${shopRoot}cart.js`, { credentials: "same-origin" })
      ).json();

      // Prefer live section HTML; rs-cart-drawer also re-fetches on cart:change.
      cartContent.sections = sectionIds.length ? await fetchCartDrawerSections(sectionIds) : responseJson.sections || {};

      document.documentElement.dispatchEvent(
        new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "variant:add",
            onSuccessDo: "force_open_drawer",
            cart: cartContent,
          },
        })
      );

      if (btn) {
        btn.innerHTML = data.addedLabel || "Added!";
        setTimeout(() => {
          btn.innerHTML = originalText;
          updateTotals();
          btn.disabled = isIndependentPack() && btn.id === "pack-add-to-cart" ? !packIsComplete() : false;
          btn.setAttribute("aria-disabled", String(btn.disabled));
          btn.removeAttribute("aria-busy");
        }, 1500);
      }

      return true;
    } catch (err) {
      console.error("RS PDP: add to cart error", err);
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      if (btn) {
        btn.disabled = isIndependentPack() && btn.id === "pack-add-to-cart" ? !packIsComplete() : false;
        btn.setAttribute("aria-disabled", String(btn.disabled));
        btn.removeAttribute("aria-busy");
      }
      return false;
    }
  };

  const addToCart = async () => {
    const product = findProduct(state.currentHandle) || data.current;
    const btn = $("#add-to-cart", root);
    if (!product.variantId || !product.available) return;
    await postCartAdd(buildCartItems(), btn);
  };

  const addPackToCart = async () => {
    if (!isIndependentPack() || !packIsComplete()) return;
    const btn = $("#pack-add-to-cart", root);
    await postCartAdd(buildPackCartItems(), btn);
  };

  const initStickyBar = () => {
    const bar = document.getElementById("sticky-bar");
    const buyBoxActions = $(".buy-box__actions", root);
    const stickyAdd = document.getElementById("sticky-add");
    const mainAdd = $("#add-to-cart", root);

    if (!bar || !buyBoxActions) return;

    const setVisible = (visible) => {
      bar.classList.toggle("is-visible", visible);
      bar.setAttribute("aria-hidden", String(!visible));
      document.body.classList.toggle("has-sticky-atc", visible);
    };

    const observer = new IntersectionObserver(
      ([entry]) => setVisible(!entry.isIntersecting),
      { threshold: 0, rootMargin: "0px" }
    );
    observer.observe(buyBoxActions);

    stickyAdd?.addEventListener("click", () => mainAdd?.click());
  };

  const init = () => {
    renderGallery();
    renderDialSwatches();
    renderBezelSwatches();
    if (data.showExtras) {
      renderBundles();
      renderExtrasPicker();
    }
    updateLabels();
    updateTotals();

    root.addEventListener("rs:countdown-change", updateTotals);
    $("#add-to-cart", root)?.addEventListener("click", addToCart);
    $("#pack-add-to-cart", root)?.addEventListener("click", addPackToCart);
    if (data.showStickyAtc) initStickyBar();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
